// Page-view counter behind the admin "Visitas web" tab.
//
// Each page view is one call to the Supabase function site_visit_hello()
// (backend/site_visits.sql). The visitor's IP is NOT sent from here: the
// database reads it from the request headers, so it can't be made up.
//
// Nothing is stored in the browser (no cookie, no localStorage): a visitor is
// an IP. Only the published sites count — localhost, previews, the Electron
// and Android shells are ignored.
import { supabase } from './supabase';

export function siteForHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (h === 'app.zcom.es') return 'app.zcom.es';
  if (h === 'zcom.es' || h === 'www.zcom.es') return 'zcom.es';
  return null;
}

// The segment after these is a credential (pay link, delivery tracking link,
// Book Me client portal /book-me/shared/<token>): it must never reach the
// visit log, whatever it looks like.
const SECRET_AFTER = new Set(['pay', 'track', 'shared']);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const looksLikeId = (seg) =>
  UUID.test(seg)
  || /^\d+$/.test(seg)
  || /^[0-9a-f]{16,}$/i.test(seg)
  || (seg.length >= 20 && /^[A-Za-z0-9_-]+$/.test(seg) && /[a-z]/.test(seg) && /[A-Z]/.test(seg) && /\d/.test(seg));

// "/invoices/view/<uuid>?x=1" → "/invoices/view/:id". The query string goes
// too: it can carry tokens and emails, and the page is what we count.
export function cleanPath(pathname) {
  const parts = String(pathname || '/').split(/[?#]/)[0].split('/').filter(Boolean);
  const out = parts.map((seg, i) => {
    if (i > 0 && SECRET_AFTER.has(parts[i - 1].toLowerCase())) return ':token';
    return looksLikeId(seg) ? ':id' : seg;
  });
  return `/${out.join('/')}`.slice(0, 200);
}

// Where the visitor came from, only when it is another website. Host and path,
// no query (search terms, tokens).
export function externalReferrer(referrer, hostname) {
  if (!referrer) return null;
  try {
    const u = new URL(referrer);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (u.hostname.toLowerCase() === String(hostname || '').toLowerCase()) return null;
    return `${u.protocol}//${u.hostname}${u.pathname}`.slice(0, 300);
  } catch {
    return null;
  }
}

// Same page again within the window = the same visit, not a new one (tab
// switching inside the app, React re-mounts). Memory only: a reload starts over.
export function createVisitGate(windowMs = 30 * 60 * 1000, now = Date.now) {
  const seen = new Map();
  return (key) => {
    const t = now();
    const last = seen.get(key);
    if (last !== undefined && t - last < windowMs) return false;
    seen.set(key, t);
    return true;
  };
}

// Builds the function arguments for one page view, or null when it must not
// be counted. `state` remembers that the referrer is reported once per load.
export function buildVisit(pathname, win, state) {
  const site = siteForHost(win?.location?.hostname);
  if (!site) return null;
  const path = cleanPath(pathname);
  if (!state.gate(path)) return null;
  const referrer = state.referrerSent ? null : externalReferrer(win.document?.referrer, win.location.hostname);
  state.referrerSent = true;
  const scr = win.screen;
  return {
    p_site: site,
    p_path: path,
    p_referrer: referrer,
    p_language: String(win.navigator?.language || '').slice(0, 20) || null,
    p_screen: scr?.width && scr?.height ? `${scr.width}x${scr.height}` : null,
  };
}

const state = { gate: createVisitGate(), referrerSent: false };

// Fire-and-forget: counting a visit must never slow down or break the page.
export function recordVisit(pathname) {
  try {
    if (typeof window === 'undefined') return;
    const args = buildVisit(pathname, window, state);
    if (!args) return;
    setTimeout(() => {
      try {
        supabase.rpc('site_visit_hello', args).then(() => {}, () => {});
      } catch { /* offline, blocked… */ }
    }, 0);
  } catch { /* never surfaces */ }
}
